import { Component, HostListener, Input, OnInit } from '@angular/core';
import { FormControl, FormControlStatus } from '@angular/forms';
import { FormFieldNumberComponent } from '../number/form-field-number.component';

@Component({
    selector: 'lib-input-counter',
    templateUrl: './form-field-counter.component.html',
    styleUrls: ['./form-field-counter.component.scss']
})
export class FormFieldCounterComponent extends FormFieldNumberComponent implements OnInit {
    readonly MIN_COUNTER_LIMIT: number = 0;
    readonly MAX_COUNTER_LIMIT: number = 999;

    @Input() snapValueBy: number = 1;
    override applyFormatting = false;
    override maxCharacterLength = 3;

    private _fakeControl = new FormControl<number>(0);
    private _min: number = this.MIN_COUNTER_LIMIT;
    private _max: number = this.MAX_COUNTER_LIMIT;
    private _initValue: number = this.MIN_COUNTER_LIMIT;

    override ngOnInit(): void {
        super.ngOnInit();

        this.formControl.statusChanges.subscribe((status: FormControlStatus) => {
            if (status !== 'DISABLED') {
                this._min = this.GetMinValidatorValue();
                this._max = this.GetMaxValidatorValue();
            }
        });

        if (!this.formControl.isRequired) {
            throw new Error('This formControl has to be Required!');
        }
    }

    override ngAfterViewInit(): void {
        super.ngAfterViewInit();

        this._min = this.GetMinValidatorValue();
        this._max = this.GetMaxValidatorValue();
        this._initValue = this.formControl.value ?? this._min;
    }

    override OnBlur() {
        super.OnBlur();

        if (!this.formControl.valid) {
            if (this.formControl.value) {
                this.SetValue(this.CorrectValue(this.formControl.value));
            } else {
                this.SetValue(this._initValue);
            }
        }
    }

    Decrement() {
        let newValue = Math.ceil((this.formControl.value ?? 0) / this.snapValueBy) * this.snapValueBy;
        this.SetValue(this.CorrectValue(newValue - this.snapValueBy));
    }

    Increment() {
        let newValue = Math.floor((this.formControl.value ?? 0) / this.snapValueBy) * this.snapValueBy;
        this.SetValue(this.CorrectValue(newValue + this.snapValueBy));
    }

    private SetValue(value: number) {
        this.formControl.isDirty = true;
        this.formControl.setValue(value);
        this.change.emit(value);
    }

    private CorrectValue(value: number): number {
        for (let i = this.MIN_COUNTER_LIMIT; i < this.MAX_COUNTER_LIMIT; i++) {
            if (this.CheckValue(value - i)) {
                return value - i;
            }
            if (this.CheckValue(value + i)) {
                return value + i;
            }
        }

        console.error('Validator does not allow for any valid counter values!');
        return value;
    }

    private CheckValue(value: number): boolean {
        if (value < this._min || value > this._max) {
            return false;
        }

        this._fakeControl.setValue(value, { emitEvent: false, emitModelToViewChange: false, emitViewToModelChange: false, onlySelf: true });
        return !this.formControl.validator || !this.formControl.validator(this._fakeControl);
    }

    private GetMaxValidatorValue(): number {
        const validator = this.formControl.validator;
        if (validator === null) {
            return this.MAX_COUNTER_LIMIT;
        }

        const errors = validator(new FormControl(Infinity)) ?? {};
        return "max" in errors && errors.max.max < this.MAX_COUNTER_LIMIT ? errors.max.max : this.MAX_COUNTER_LIMIT;
    }

    private GetMinValidatorValue(): number {
        const validator = this.formControl.validator;
        if (validator === null) {
            return this.MIN_COUNTER_LIMIT;
        }

        const errors = validator(new FormControl(-Infinity)) ?? {};
        return "min" in errors && errors.min.min > this.MIN_COUNTER_LIMIT ? errors.min.min : this.MIN_COUNTER_LIMIT;
    }

    get minReached(): boolean {
        return this.formControl.value === null || this._min === this.formControl.value;
    }

    get maxReached(): boolean {
        return this.formControl.value === null || this._max === this.formControl.value;
    }

    @HostListener('keydown', ['$event'])
    handleKeyboardEvent(event: KeyboardEvent) {
        if (event.key === 'ArrowUp' || event.key === '+') {
            this.Increment();
        } else if (event.key === 'ArrowDown' || event.key === '-') {
            this.Decrement();
        }
    }
}
